/**
 * THE THIN ENTRY. The only file in this repository that knows the voyage layer is running
 * in a browser.
 *
 * CL2-R2-A: after two rounds, neither of the two verbs this lane owns was reachable from a
 * browser. `index.html` loaded `/src/main.ts`, which imports eight of the lane's modules
 * and constructs no session, no world and no order, so a player could never moor at an
 * island and never press the exit control. Everything the lane had proved was proved about
 * a state space no execution path could enter.
 *
 * This file closes that. It is deliberately as small as it can be and does four things:
 *
 *   1. Adapts `window.localStorage` to sim-core's `SaveStorage` port - three one-line
 *      methods, all allowed to throw, because the session catches.
 *   2. Reads the host clock, which is the ONE place in this codebase that may.
 *   3. Opens a `VoyageSession` and, on request, drives it on an interval.
 *   4. Publishes both verbs on `window.reelin`, so the lane is reachable from a real
 *      browser today rather than after some other lane ships a UI.
 *
 * -- WHY IT IS NOT A UI, AND WHY THAT IS NOT A DODGE --------------------------
 *
 * IT DRAWS NOTHING. Not one element, not one style. CONTRACT.md section 1.2 gates every
 * visual decision behind `art-technique`, and a plausible-looking mock built before that
 * gate is work that gets thrown away and, worse, anchors expectations to something nobody
 * measured. `surfaces` owns the HUD and binds it to exactly the API below.
 *
 * What that leaves is a real, executable path from the app entry to both verbs, provable
 * in a real browser - `tools/lanes/core-loop/browser-proof.mjs` drives it in headless
 * Chromium, sails to a charted place, reloads the page, and reads the destination back out
 * of the save. That script is the evidence, and its output is committed beside it.
 *
 * -- THE CLOCK IS AN ARGUMENT EVERYWHERE BUT HERE -----------------------------
 *
 * `openVoyage` takes an optional `nowMs`. That is not a test hook: sim-core's session and
 * this lane's session both take the host's clock as an argument by design, precisely so
 * that a worker, a headless harness or a host with a different clock can drive them. This
 * file is the host, so this file is where `Date.now()` is read - once per call, never in a
 * loop body, so a frame cannot read two different instants.
 */

import { MAX_SEED, SAVE_KEYS, importSave, type SaveStorage } from '../sim/index';
import { openVoyageSession, type VoyageSession, type VoyageSessionOptions } from '../voyage/index';
import { openPlayerSession, type PlayerSession } from './player-session';

/**
 * `window.localStorage` as a `SaveStorage`.
 *
 * Every method is allowed to throw - a private window, a locked-down browser and a full
 * quota all do - and every caller of this port already treats a throw as a note rather
 * than an exception. Wrapping them here would hide which of the two happened.
 */
export function browserSaveStorage(): SaveStorage {
  return {
    read: (key: string) => window.localStorage.getItem(key),
    write: (key: string, value: string) => {
      window.localStorage.setItem(key, value);
    },
    remove: (key: string) => {
      window.localStorage.removeItem(key);
    },
  };
}

/**
 * A seed for a run that has never been saved.
 *
 * Drawn from the host clock rather than from `Math.random`, so a run is reproducible from
 * one recorded number. It only ever matters once: from the first write onward the seed
 * lives in the save, and the world is built from THAT - see `simVoyageWorldFor`.
 */
export function seedFromClock(nowMs: number): number {
  return Math.trunc(nowMs) >>> 0 || 1;
}

export interface OpenVoyageOptions extends Partial<Omit<VoyageSessionOptions, 'storage'>> {
  readonly storage?: SaveStorage;
}

/** Open a session against the browser's own storage and clock. Draws nothing. */
export function openVoyage(options: OpenVoyageOptions = {}): VoyageSession {
  const nowMs = options.nowMs ?? Date.now();
  const seed = Math.min(options.seed ?? seedFromClock(nowMs), MAX_SEED);
  return openVoyageSession({
    storage: options.storage ?? browserSaveStorage(),
    nowMs,
    seed,
    ...(options.key === undefined ? {} : { key: options.key }),
    ...(options.autosaveIntervalMs === undefined
      ? {}
      : { autosaveIntervalMs: options.autosaveIntervalMs }),
  });
}

/**
 * Open the product-owned session: profile, settings, recovery and the voyage under them.
 *
 * OPENING IS NOT STARTING. This constructs the session and reads the save; it writes nothing
 * (sim-core's `openSession` writes only on a tick, a choice or an explicit save, and on the
 * one migration path that carries a save forward from an older key). The shell opens it at
 * boot so Settings and Save and Recovery work before a voyage runs, and the voyage - the
 * ticking, the autosave - begins only on Continue Voyage or Set Sail (`startPlayerVoyage`).
 *
 * `clock` is the host's clock, read by the session for every reconcile and save stamp, so a
 * page that sat on the title for ten minutes stamps its first save at the time it was made
 * and not at the time the page loaded.
 */
export function openPlayerVoyage(options: OpenVoyageOptions & { readonly clock?: () => number } = {}): PlayerSession {
  const nowMs = options.nowMs ?? (options.clock ?? Date.now)();
  const seed = Math.min(options.seed ?? seedFromClock(nowMs), MAX_SEED);
  return openPlayerSession({
    storage: options.storage ?? browserSaveStorage(), nowMs, seed,
    ...(options.key === undefined ? {} : { key: options.key }),
    ...(options.clock === undefined ? {} : { now: options.clock }),
  });
}

/**
 * Whether Continue Voyage has anything to continue: a save on the real key chain that
 * imports cleanly. A present-but-corrupt save is NOT a valid save - offering Continue on it
 * would open a fresh run and call it the player's. Storage that throws reads as no save.
 */
export function hasValidSave(storage: SaveStorage = browserSaveStorage(), nowMs = Date.now(), keys: readonly string[] = SAVE_KEYS): boolean {
  for (const key of keys) {
    let text: string | null;
    try { text = storage.read(key); } catch { return false; }
    if (text !== null) return importSave(text, nowMs).ok;
  }
  return false;
}

/**
 * Storage that forgets: the title's preview voyage lives here, so looking at the sea before
 * choosing to sail writes nothing to the browser. A fresh page, a fresh seed, no save.
 */
export function previewStorage(): SaveStorage {
  const bytes = new Map<string, string>();
  return {
    read: (key) => bytes.get(key) ?? null,
    write: (key, value) => { bytes.set(key, value); },
    remove: (key) => { bytes.delete(key); },
  };
}

export interface RunningVoyage {
  readonly session: VoyageSession;
  /** Stop the driver. The save is written one last time first. */
  stop(): void;
}

/**
 * Open a session and advance it on an interval.
 *
 * ONE SECOND, not one frame. The simulation settles from timestamps, so calling it sixty
 * times a second buys nothing at all and costs a wake-up per frame; a background tab that
 * gets one call a minute lands on exactly the same state. A renderer that wants a fresh
 * boat position every frame calls `session.view(Date.now())`, which advances nothing.
 *
 * `clock` (world-render round 2): a host that owns a different clock hands it in, and every
 * read this driver makes goes through it. The world page (`world-entry.ts`) keeps the dev
 * harness's time controls - run at 60x, +1 hour, +1 day - as an offset on the wall clock, and
 * the session it drives has to tick on the SAME clock its frames are viewed at, or the save
 * and the picture would disagree about what time it is. The default is still `Date.now`, read
 * here and nowhere in the voyage layer.
 */
export function startVoyage(
  options: OpenVoyageOptions & { readonly intervalMs?: number; readonly clock?: () => number } = {},
): RunningVoyage {
  const clock = options.clock ?? Date.now;
  const session = openVoyage({ ...options, nowMs: options.nowMs ?? clock() });
  const intervalMs = options.intervalMs ?? 1000;
  session.tick(options.nowMs ?? clock());
  const timer = setInterval(() => session.tick(clock()), intervalMs);
  return {
    session,
    stop(): void {
      clearInterval(timer);
      session.save(clock());
    },
  };
}

/**
 * A `RunningVoyage` over a `PlayerSession`: the same one-second driver, but every verb the
 * renderer's chart control calls goes through the session's own command boundary, so the
 * profile is reconciled before a target changes and a local choice is recorded (Task 3).
 *
 * `session` is a facade over `player.voyage()`, read on every call rather than captured,
 * because an import or a new voyage swaps the VoyageSession underneath and the world page
 * holds one `running` for the life of the tab.
 */
export interface RunningPlayerVoyage extends RunningVoyage {
  readonly player: PlayerSession;
}

export function startPlayerVoyage(player: PlayerSession, options: { readonly intervalMs?: number; readonly clock?: () => number } = {}): RunningPlayerVoyage {
  const clock = options.clock ?? Date.now;
  const v = (): VoyageSession => player.voyage();
  const session: VoyageSession = {
    get openNotes() { return v().openNotes; },
    world: () => v().world(),
    state: () => v().state(),
    envelope: () => v().envelope(),
    order: () => v().order(),
    view: (nowMs) => v().view(nowMs),
    tick: (nowMs) => player.tick(nowMs),
    sailTo: (placeId, nowMs) => player.sailTo(placeId, nowMs),
    returnToTrack: (nowMs) => player.returnToNaturalCourse(nowMs),
    setTarget: (speciesId, nowMs) => player.setTarget(speciesId, nowMs),
    exportText: () => player.exportSave(),
    importText: (text, nowMs) => (typeof text === 'string' ? player.importSave(text) : v().importText(text, nowMs)),
    save: () => player.save(),
    lastSavedMs: () => v().lastSavedMs(),
  };
  const intervalMs = options.intervalMs ?? 1000;
  player.tick(clock());
  const timer = setInterval(() => player.tick(clock()), intervalMs);
  return {
    session,
    player,
    stop(): void {
      clearInterval(timer);
      player.save();
    },
  };
}

/**
 * The surface published on `window.reelin`.
 *
 * It is the session's own API and adds nothing to it, so anything reachable here is
 * reachable from a UI and vice versa. It exists so that the lane's two verbs are
 * executable in a browser before `surfaces` has drawn anything - which is the difference
 * between a module with a caller and a module without one.
 */
export interface ReelinGlobal {
  readonly open: typeof openVoyage;
  readonly start: typeof startVoyage;
  /** The session `start` opened, if one is running. */
  running: RunningVoyage | null;
}

declare global {
  // eslint-disable-next-line no-var
  var reelin: ReelinGlobal | undefined;
}

export function installVoyageGlobal(): ReelinGlobal {
  const api: ReelinGlobal = {
    open: openVoyage,
    start: (options = {}) => {
      const run = startVoyage(options);
      api.running = run;
      return run;
    },
    running: null,
  };
  globalThis.reelin = api;
  return api;
}

// Installed on import, inside a guard, so a host without a window (a build step, a test
// runner, a worker) can import this module for its adapters without it reaching for one.
if (typeof window !== 'undefined') installVoyageGlobal();
